let beepCtx = null;

const ensureCtx = () => {
  if (!beepCtx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    beepCtx = new Ctor();
  }
  if (beepCtx.state === 'suspended') {
    beepCtx.resume().catch(() => {});
  }
  return beepCtx;
};

// Browser autoplay policy starts every AudioContext "suspended" until a user
// gesture resumes it. Scan beeps fire from a WebSocket handler (no gesture),
// so without this priming the context stays suspended and nothing plays.
//
// We must keep the context primed for the WHOLE session, not just once: the
// browser re-suspends an AudioContext on tab switch, window blur, OS audio-
// focus loss, or inactivity. If we unhooked after the first interaction, a
// later beep would find the context suspended, call resume() (whose promise
// only settles on the NEXT user gesture), and the sound would be deferred
// until "the user does something". So we resume on every gesture (a cheap
// no-op when already running) and again whenever the tab becomes visible.
if (typeof window !== 'undefined') {
  const prime = () => {
    ensureCtx();
  };
  window.addEventListener('pointerdown', prime, { passive: true });
  window.addEventListener('keydown', prime);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') ensureCtx();
    });
  }
}

// Run `schedule(ctx)` once the context is actually running. If it's still
// suspended, wait for resume() to resolve so notes are scheduled against a
// live (advancing) clock instead of a frozen t=0.
const whenRunning = (ctx, schedule) => {
  if (ctx.state === 'running') {
    schedule(ctx);
    return;
  }
  ctx
    .resume()
    .then(() => schedule(ctx))
    .catch(() => {});
};

/**
 * Two low square-wave buzzes (~A3) — distinct from any soft confirmation
 * tone. Used by the Rx Scan Station whenever a scan needs operator attention
 * (missing lot/drug, drug-name mismatch, unknown container type).
 * Audio is best-effort; failures are swallowed.
 */
export const playWarningBeep = () => {
  try {
    const ctx = ensureCtx();
    if (!ctx) return;
    whenRunning(ctx, () => {
      // First tone: the same low square buzz as a hard error, so the operator
      // recognises "something's off".
      const buzz = (startOffset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'square';
        osc.frequency.value = 220;
        const t = ctx.currentTime + startOffset;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.18, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.18);
      };
      // Second tone: a falling triangle tone — clearly downward, so it reads
      // as "caution" and never gets mistaken for the rising success chirp.
      const fallTone = (startOffset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        const t = ctx.currentTime + startOffset;
        osc.frequency.setValueAtTime(523, t);
        osc.frequency.exponentialRampToValueAtTime(330, t + 0.22);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.22, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.26);
      };
      buzz(0);
      fallTone(0.22);
    });
  } catch {
    /* best-effort */
  }
};

/**
 * Two short rising sine chirps (~880Hz → 1175Hz) — a pleasant confirmation.
 * Played when a drug vial scans cleanly (lot + drug present, drug matches the
 * Rx, and the label was confidently matched). Best-effort; failures swallowed.
 */
export const playSuccessBeep = () => {
  try {
    const ctx = ensureCtx();
    if (!ctx) return;
    whenRunning(ctx, () => {
      const chirp = (startOffset, freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = ctx.currentTime + startOffset;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.2, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.14);
      };
      chirp(0, 880);
      chirp(0.16, 1175);
    });
  } catch {
    /* best-effort */
  }
};
